// Task 11 — one row in the /notes inbox: active notes render sender +
// timestamp, body, optional doc-context line (with a Link to the
// verification station when documentId is set), a reply textarea, and an
// Acknowledge button. Acked (history) rows are read-only: body + "Acknowledged
// <time>" + reply text, no controls.
import { useState } from "react";
import { Link } from "react-router";
import { isDirectedNoteActive, DIRECTED_NOTE_REPLY_MAX, type DirectedNoteRecord } from "@capture/shared";
import { formatTimestamp } from "@/lib/formatters";
import styles from "../directed-notes.module.css";

export interface NoteInboxItemProps {
  note: DirectedNoteRecord;
  onAcknowledge: (id: string, reply?: string) => Promise<void>;
}

export function NoteInboxItem({ note, onAcknowledge }: NoteInboxItemProps) {
  const [reply, setReply] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const active = isDirectedNoteActive(note);

  if (!active) {
    return (
      <div className={styles.noteItem}>
        <p className={styles.noteBody}>{note.body}</p>
        <p className={styles.noteAcked}>Acknowledged {formatTimestamp(note.acknowledgedAt)}</p>
        {note.reply && <p className={styles.noteReply}>{note.reply}</p>}
      </div>
    );
  }

  const handleAcknowledge = async () => {
    setSubmitting(true);
    try {
      await onAcknowledge(note.id, reply.trim() || undefined);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`${styles.noteItem} ${styles.noteItemActive}`}>
      <div className={styles.noteMeta}>
        <span className={styles.noteSender}>{note.createdByName ?? "A supervisor"}</span>
        <span className={styles.noteTimestamp}>{formatTimestamp(note.createdAt)}</span>
      </div>
      <p className={styles.noteBody}>{note.body}</p>
      {note.context && (
        <p className={styles.noteContext}>
          {[note.context.filename, note.context.jobTypeName, note.context.stepType].filter(Boolean).join(" · ")}
          {note.documentId && (
            <>
              {" "}
              <Link to={`/verify/${note.documentId}`}>Open document</Link>
            </>
          )}
        </p>
      )}
      <div className={styles.replyRow}>
        <textarea
          value={reply}
          onChange={(e) => setReply(e.target.value.slice(0, DIRECTED_NOTE_REPLY_MAX))}
          maxLength={DIRECTED_NOTE_REPLY_MAX}
          placeholder="Optional reply…"
        />
        <span className={styles.counter}>{reply.length} / {DIRECTED_NOTE_REPLY_MAX}</span>
      </div>
      <button className={styles.ackButton} onClick={() => void handleAcknowledge()} disabled={submitting}>
        Acknowledge
      </button>
    </div>
  );
}
